import axios from 'axios';
import { OAuthCodeGenerationUtils } from './OAuthCodeGenerationUtils';
import { OAuthTokenGenerationConstants } from './constants/OAuthTokenGenerationConstants';

export class OAuthTokenGeneration {
    static async initiateCodeChallenge(OAuthProviderVariables) {
        try {

            const config = await OAuthCodeGenerationUtils.getConfig(OAuthProviderVariables.ISAM_DOMAIN);
            const state = OAuthCodeGenerationUtils.randomString(32);
            const codeVerifier = OAuthCodeGenerationUtils.randomString(32);
            const codeChallenge = await OAuthCodeGenerationUtils.sha256(codeVerifier).then(OAuthCodeGenerationUtils.bufferToBase64UrlEncoded);

            // we need to store the state to validate the callback
            // and also the code verifier to send later
            sessionStorage.setItem(`login-code-verifier-${state}`, codeVerifier);

            const newStandardUrl = getRebrandedUrl(config.authorization_endpoint);
            const authorizationEndpointUrl = new URL(newStandardUrl);

            // here we encode the authorization request
            authorizationEndpointUrl.search = new URLSearchParams({
                audience: OAuthProviderVariables.API_AUDIENCE,
                redirect_uri: OAuthProviderVariables.REDIRECT_URI,
                client_id: OAuthProviderVariables.ISAM_CLIENT_ID,
                response_type: OAuthTokenGenerationConstants.RESPONSE_TYPE,
                scope: OAuthTokenGenerationConstants.OPENID_SCOPE,
                code_challenge: codeChallenge,
                code_challenge_method: OAuthTokenGenerationConstants.CODE_CHALLENGE_METHOD,
                state
            });

            window.location.assign(authorizationEndpointUrl);
        } catch (err) {
            console.error(`initiateCodeChallenge failed to generate authorization code: ${err}`);
        }
    }

    static async generateOauthToken(OAuthProviderVariables) {
        try {
            const search = new URLSearchParams(window.location.search);
            if (!search.has('code')) { return; }
            const code = search.get('code');
            const state = search.get('state');
            const code_verifier = sessionStorage.getItem(`login-code-verifier-${state}`);

            if (!code_verifier) {
                throw new Error('unexpected state parameter');
            }

            const config = await OAuthCodeGenerationUtils.getConfig(OAuthProviderVariables.ISAM_DOMAIN);

            // construct the body and exchange the authorization code for a token set
            const body = new URLSearchParams({
                audience: OAuthProviderVariables.API_AUDIENCE,
                client_id: OAuthProviderVariables.ISAM_CLIENT_ID,
                redirect_uri: OAuthProviderVariables.REDIRECT_URI,
                grant_type: OAuthTokenGenerationConstants.GRANT_TYPE,
                code_verifier,
                code
            });

            const tokenEndpoint = getRebrandedUrl(config.token_endpoint);
            
            const tokenSet = await axios.post(tokenEndpoint, body);

            window.tokenSet = tokenSet;
            window.verifier = code_verifier;

            let access_token = null; let id_token = null;

            // Check for access token and store it in localStorage
            if (tokenSet && tokenSet.data && tokenSet.data.access_token) {
                [access_token] = [tokenSet.data.access_token];
                localStorage.setItem('access_token', `Bearer ${tokenSet.data.access_token}`);
            }

            // Check for idToken and store it in localStorage
            if (tokenSet && tokenSet.data && tokenSet.data.id_token) {
                [id_token] = [tokenSet.data.id_token];
                localStorage.setItem('id_token', `${tokenSet.data.id_token}`);
            }

            // remove the querystring from the url in the address bar
            const url = new URL(window.location);
            url.search = '';
            window.history.pushState('', document.title, url);
            const tokens = { access_token, id_token };
            return tokens;
        } catch (err) {
            console.error(`generateOauthToken failed to generate token: ${err}`);
        }
    }

    static async initiateRefreshFlow(OAuthProviderVariables) {
        try {
            const config = await OAuthCodeGenerationUtils.getConfig(OAuthProviderVariables.ISAM_DOMAIN);

            const state = OAuthCodeGenerationUtils.randomString(32);
            const codeVerifier = OAuthCodeGenerationUtils.randomString(32);
            const codeChallenge = await OAuthCodeGenerationUtils.sha256(codeVerifier).then(OAuthCodeGenerationUtils.bufferToBase64UrlEncoded);
            
            const newStandardUrl = getRebrandedUrl(config.authorization_endpoint);

            const authorizationEndpointUrl = new URL(newStandardUrl);

            // we need to store the state to validate the callback
            // and also the code verifier to send later
            sessionStorage.setItem(`login-code-verifier-${state}`, codeVerifier);

            // here we encode the authorization request
            authorizationEndpointUrl.search = new URLSearchParams({
                audience: OAuthProviderVariables.API_AUDIENCE,
                redirect_uri: OAuthProviderVariables.REDIRECT_URI,
                client_id: OAuthProviderVariables.ISAM_CLIENT_ID,
                response_type: OAuthTokenGenerationConstants.RESPONSE_TYPE,
                response_mode: OAuthTokenGenerationConstants.RESPONSE_MODE,
                scope: OAuthTokenGenerationConstants.OPENID_SCOPE,
                prompt: OAuthTokenGenerationConstants.PROMPT,
                code_challenge: codeChallenge,
                code_challenge_method: OAuthTokenGenerationConstants.CODE_CHALLENGE_METHOD,
                state
            });

            // Send the request in an iFrame
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.setAttribute('width', '0');
            iframe.setAttribute('height', '0');
            window.document.body.appendChild(iframe);
            iframe.setAttribute('src', authorizationEndpointUrl);
        } catch (err) {
            console.error(`initiateRefreshFlow failed to renew token: ${err}`);
        }
    }
}

// function to update the URL from allstate to standard for pre-prod and prod environments
export function getRebrandedUrl(baseUrl) {
  const url = new URL(baseUrl);

  if (
    url.hostname === 'conacc-st.allstate.com' ||
    url.hostname === 'conacc.allstate.com' ||
    url.hostname === 'agtacc-st.allstate.com' ||
    url.hostname === 'agtacc.allstate.com'
  ) {
    if (url.pathname === '/mga/sps/oauth/oauth20/authorize' || url.pathname === '/mga/sps/oauth/oauth20/token') {
      url.hostname = url.hostname
        .replace('-st.allstate.com', '-pre-prod.standard.com')
        .replace('.allstate.com', '.standard.com');
      return url.toString();
    }
    return baseUrl;
  }
    return baseUrl;
}
